import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { Box, CircularProgress, Tab, Tabs, Typography, useTheme } from '@mui/material';

export type GetTabValueProps = { buildingId: number | null; status: 'active' | 'inactive' | 'pending' };

interface ZonesTabProps {
  buildingId: number | null;
  getTabValue: (props: GetTabValueProps) => string;
  getCurrentTabValue: () => string;
  loading: boolean;
  counts: {
    active: number;
    inactive: number;
  };
}

export const ZonesTab = ({ buildingId, getTabValue, getCurrentTabValue, loading, counts }: ZonesTabProps) => {
  const theme = useTheme();
  const { width } = useWindowSize();
  const navigate = useNavigate();

  const tabs = [
    {
      id: 'active',
      label: 'Active',
      count: counts.active,
      value: getTabValue({ buildingId, status: 'active' }),
    },
    {
      id: 'inactive',
      label: 'Inactive',
      count: counts.inactive,
      value: getTabValue({ buildingId, status: 'inactive' }),
    },
  ];

  const tabValues = tabs.map((tab) => tab.value);
  const tabValue = getCurrentTabValue();

  if (tabValues.includes(tabValue)) {
    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'white',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Tabs value={tabValue}>
          {tabs.map((tab) => (
            <Tab
              key={`zone-table-tab-${tab.id}`}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {width >= theme.breakpoints.values.sm && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor:
                          tab.value === tabValue ? theme.palette.primary.main : theme.palette.text.secondary,
                        borderRadius: 8,
                        marginRight: 2,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      color="testSecondary"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize:
                          width < theme.breakpoints.values.sm
                            ? theme.typography.body2.fontSize
                            : theme.typography.body1.fontSize,
                        marginRight: 1,
                      }}
                    >
                      {tab.label}
                    </Typography>

                    {loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Typography
                        color="testSecondary"
                        sx={{
                          ...{ textTransform: 'none', fontWeight: 600 },
                          ...{
                            fontSize:
                              width < theme.breakpoints.values.sm
                                ? theme.typography.body2.fontSize
                                : theme.typography.body1.fontSize,
                          },
                        }}
                      >
                        ({tab.count})
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
              value={tab.value}
              sx={{
                flex: 1,
                maxWidth: 'unset',
                paddingX: width < theme.breakpoints.values.sm ? 1 : 2,
              }}
              onClick={() => navigate(tab.value)}
            />
          ))}
        </Tabs>
      </Box>
    );
  }

  return null;
};
